import { useState } from 'preact/hooks';
import {
  BanknoteIcon,
  BarcodeIcon,
  CheckIcon,
  DollarSignIcon,
  GemIcon,
  LockIcon,
  RectangleEllipsisIcon,
  ShoppingBagIcon,
  ThumbsUpIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';
import { AppShell } from '@/flows/marketplace/components/app-shell.js';
import { AddressFields } from '@/components/address-fields.js';
import { Alert } from '@/flows/marketplace/components/alert.js';
import { Button } from '@/components/button.js';
import { Card } from '@/flows/marketplace/components/card.js';
import { Field, FieldError, FieldFrame } from '@/components/field.js';
import { PageHero } from '@/flows/marketplace/components/page-hero.js';
import { ProgressBar } from '@/flows/marketplace/components/progress-bar.js';
import { Select } from '@/components/select.js';
import { SegmentedControl } from '@/flows/marketplace/components/segmented-control.js';
import { SurchargeCheck } from '@/components/surcharge-check.js';
import { ApiError, getApiClient } from '@/lib/api-client.js';
import { rateErrorCopy, type ErrorCopy } from '@/lib/error-copy.js';
import { US_STATES, type TypedFieldSpec } from 'shared';
import { formatInteger } from '@/lib/formatters.js';
import { parseMileage } from '@/lib/mileage.js';
import { formatVin } from 'shared';
import type { AddressFormState } from '@/lib/customer-form.js';
import {
  emptyRateInputs,
  hasRateInputErrors,
  noRateInputErrors,
  toRateInputsWire,
  validateRateInputs,
  type RateInputErrors,
  type RateInputsState,
} from '@/lib/rate-inputs.js';
import { config, rateInputRequirements } from '@/state/config.js';
import { rateInputs } from '@/state/rate-inputs.js';
import { seedCustomerAddress } from '@/state/customer-form.js';
import { applyRateResponse, vehicle } from '@/state/session.js';
import { step } from '@/flows/marketplace/state/step.js';

type LookupMode = 'plate' | 'vin';

interface FieldErrors {
  plate?: string;
  state?: string;
  vin?: string;
  mileage?: string;
}

const YES_NO = [
  { value: 'yes' as const, label: 'Yes' },
  { value: 'no' as const, label: 'No' },
];

export function EntryMarketplace() {
  const [mode, setMode] = useState<LookupMode>('plate');
  const [plate, setPlate] = useState('');
  const [usState, setUsState] = useState('');
  const [vinInput, setVinInput] = useState('');
  const [mileage, setMileage] = useState('');
  const [mileageFocused, setMileageFocused] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [alert, setAlert] = useState<ErrorCopy | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const requirements = rateInputRequirements.value;
  const [inputs, setInputs] = useState<RateInputsState>(emptyRateInputs);
  const [rateErrors, setRateErrors] = useState<RateInputErrors>(noRateInputErrors);

  function clearFieldError(key: keyof FieldErrors) {
    if (errors[key] !== undefined) setErrors((p) => ({ ...p, [key]: undefined }));
  }

  function clearRateError(group: keyof RateInputErrors, key: string) {
    setRateErrors((p) => ({ ...p, [group]: { ...p[group], [key]: undefined } }));
  }

  function updateAddress(key: keyof AddressFormState, value: string) {
    setInputs((p) => ({ ...p, address: { ...p.address, [key]: value } }));
    clearRateError('address', key);
  }

  function updateField(key: string, value: string | number | boolean) {
    setInputs((p) => ({ ...p, fields: { ...p.fields, [key]: value } }));
    clearRateError('fields', key);
  }

  function toggleAttestation(line: string) {
    setInputs((p) => ({ ...p, attested: { ...p.attested, [line]: !p.attested[line] } }));
    clearRateError('attestations', line);
  }

  /** In plate mode the VIN isn't known yet — `handleSubmit` fills it from the decode. */
  function validate(): { vin: string; mileage: number } | null {
    const next: FieldErrors = {};
    const mileageInt = parseMileage(mileage);
    if (mileageInt === null) {
      next.mileage = 'Enter your current mileage to continue.';
    }

    if (mode === 'plate') {
      if (plate.trim().length < 1) next.plate = 'Enter your full plate number.';
      if (usState.length !== 2) next.state = 'Select your state.';
    } else {
      const normalizedVin = formatVin(vinInput);
      if (normalizedVin.length !== 17) {
        next.vin = 'VIN must be exactly 17 characters.';
      }
    }

    const nextRateErrors = validateRateInputs(requirements, inputs);
    setErrors(next);
    setRateErrors(nextRateErrors);

    // The `mileageInt` leg is redundant with the error check above; it's what
    // narrows the type for the return below.
    if (Object.keys(next).length > 0 || hasRateInputErrors(nextRateErrors) || mileageInt === null) {
      return null;
    }

    return { vin: mode === 'vin' ? formatVin(vinInput) : '', mileage: mileageInt };
  }

  async function handleSubmit(event: Event) {
    event.preventDefault();
    if (submitting) return;
    setAlert(null);
    const validated = validate();
    if (!validated) return;

    const client = getApiClient();
    setSubmitting(true);

    try {
      let resolvedVin = validated.vin;
      let decodedYear: number | null = null;
      let decodedMake: string | null = null;
      let decodedModel: string | null = null;

      if (mode === 'plate') {
        try {
          const decoded = await client.decodePlate({
            plate: plate.trim(),
            state: usState,
          });
          resolvedVin = decoded.vin;
          decodedYear = decoded.year ?? null;
          decodedMake = decoded.make ?? null;
          decodedModel = decoded.model ?? null;
        } catch (err) {
          handleDecodeError(err);
          return;
        }
      }

      const rateResponse = await client.rate({
        flow_type: 'marketplace',
        vehicle: {
          vin: resolvedVin,
          mileage: validated.mileage,
        },
        rate_inputs: toRateInputsWire(requirements, inputs),
      });

      vehicle.value = {
        vin: resolvedVin,
        mileage: validated.mileage,
        year: rateResponse.decoded_vehicle?.year ?? decodedYear,
        make: rateResponse.decoded_vehicle?.make ?? decodedMake,
        model: rateResponse.decoded_vehicle?.model ?? decodedModel,
      };
      // After the call, so this always describes what the current tiers were rated with.
      rateInputs.value = inputs;
      if (requirements.buyer_address) seedCustomerAddress(inputs.address);
      applyRateResponse(rateResponse, 'clear');
      step.value = 'tiers';
    } catch (err) {
      setAlert(rateErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleDecodeError(err: unknown) {
    if (err instanceof ApiError && err.code === 'PLATE_NOT_FOUND') {
      setErrors((prev) => ({
        ...prev,
        plate: "We couldn't find that plate. Double-check it and try again.",
      }));
      return;
    }
    if (err instanceof ApiError && err.code === 'DECODE_UNAVAILABLE') {
      setAlert({
        title: "We couldn't look up that plate right now",
        message:
          "Our plate-lookup partner didn't respond. Try again, or switch to VIN mode if you have it handy.",
      });
      return;
    }
    setAlert(rateErrorCopy(err));
  }

  const vinNormalized = formatVin(vinInput);
  const vinCount = vinNormalized.length;
  const vinComplete = vinCount === 17;
  const accountName = config.value?.accountName ?? '';

  return (
    <AppShell>
      <PageHero
        title="Protect your vehicle. See your options in minutes."
        subtitle={`Shop vehicle protection plans available through ${accountName}. Enter a few details about your vehicle to see available coverage and pricing.`}
      />

      <ProgressBar />

      <Card>
        {alert !== null && <Alert copy={alert} />}

        <form className="sm-stack sm-form" onSubmit={handleSubmit} noValidate>
          <div className="sm-stack sm-section">
            <SegmentedControl<LookupMode>
              key="lookup-mode"
              ariaLabel="Lookup method"
              value={mode}
              onChange={(next) => {
                setMode(next);
                setErrors({});
                setAlert(null);
              }}
              options={[
                { value: 'plate', label: 'Plate', icon: RectangleEllipsisIcon },
                { value: 'vin', label: 'VIN', icon: BarcodeIcon },
              ]}
            />

            {/* Every child of this section is keyed. The two lookup branches render different
                element types at the same index (a `div` wrapper for plate, a bare `Field` for
                VIN), and unkeyed diffing matches by type across indices — so the VIN field
                would adopt the mileage field's instance and DOM node, leaving the re-created
                mileage input desynced from its controlled `value` and impossible to type in. */}
            {mode === 'plate' ? (
              <div className="sm-entry-row" key="plate-row">
                <Field
                  id="sm-plate"
                  label="License plate"
                  labelAux="Required"
                  hint="We use your plate to look up your vehicle's VIN."
                  inputClassName="sm-input-mono"
                  maxLength={8}
                  placeholder="ABC 1234"
                  autoComplete="off"
                  spellcheck={false}
                  value={plate}
                  error={errors.plate}
                  onInput={(e) => {
                    setPlate((e.currentTarget as HTMLInputElement).value);
                    clearFieldError('plate');
                  }}
                />
                <Select
                  id="sm-entry-state"
                  label="State"
                  labelAux="Required"
                  placeholder="Select"
                  options={US_STATES}
                  value={usState}
                  error={errors.state}
                  onChange={(e) => {
                    setUsState((e.currentTarget as HTMLSelectElement).value);
                    clearFieldError('state');
                  }}
                />
              </div>
            ) : (
              <Field
                key="vin"
                id="sm-vin"
                label="VIN"
                labelAux="Required"
                hint="Find your VIN on the driver's-side dashboard (visible through the windshield), the driver's door jamb sticker, or your registration or insurance card."
                inputClassName="sm-input-mono sm-input-vin"
                maxLength={17}
                placeholder="1HGCM82633A123456"
                autoComplete="off"
                spellcheck={false}
                value={vinInput}
                error={errors.vin}
                onInput={(e) => {
                  setVinInput((e.currentTarget as HTMLInputElement).value);
                  clearFieldError('vin');
                }}
                trailingSlot={
                  <span
                    className="sm-type-caption sm-tabular sm-input-counter"
                    data-state={vinComplete ? 'complete' : undefined}
                  >
                    {vinCount} / 17
                  </span>
                }
              />
            )}

            <Field
              key="mileage"
              id="sm-mileage"
              label="Current mileage"
              labelAux="Required"
              hint="The number on your odometer. It helps determine which plans your vehicle qualifies for."
              inputMode="numeric"
              placeholder="42,500"
              // Raw digits while focused, comma-grouped on blur — reformatting
              // every keystroke dropped characters under fast input (caret jump).
              value={mileage === '' || mileageFocused ? mileage : formatInteger(Number(mileage))}
              error={errors.mileage}
              onFocus={() => setMileageFocused(true)}
              onBlur={() => setMileageFocused(false)}
              onInput={(e) => {
                const digits = (e.currentTarget as HTMLInputElement).value.replace(/[^0-9]/g, '');
                setMileage(digits);
                clearFieldError('mileage');
              }}
            />

            {requirements.fields.map((spec) => (
              <RateField
                key={spec.key}
                spec={spec}
                value={inputs.fields[spec.key]}
                error={rateErrors.fields[spec.key]}
                onChange={(v) => updateField(spec.key, v)}
              />
            ))}

            {requirements.buyer_address && (
              <AddressFields
                key="rate-address"
                idPrefix="sm-entry"
                labelAux="Required"
                values={inputs.address}
                errors={rateErrors.address}
                onChange={updateAddress}
              />
            )}

            {requirements.attestations.map((line) => {
              const error = rateErrors.attestations[line];
              return (
                <div className="sm-stack sm-field" key={`attest-${line}`}>
                  <div className="sm-attest">
                    <SurchargeCheck
                      checked={inputs.attested[line] === true}
                      onToggle={() => toggleAttestation(line)}
                      label={line}
                    />
                    <span className="sm-type-body">{line}</span>
                  </div>
                  {error !== undefined && <FieldError text={error} />}
                </div>
              );
            })}
          </div>

          <div className="sm-stack sm-cta-block">
            <Button type="submit" loading={submitting}>
              {submitting ? 'Fetching plans…' : 'Continue'}
            </Button>
            {submitting ? (
              <p className="sm-type-caption sm-cta-note">
                Checking plans across multiple providers — this usually takes a few seconds.
              </p>
            ) : (
              <div className="sm-stack sm-entry-assurance">
                <ul className="sm-entry-promises">
                  {PROMISES.map(({ icon: Icon, text }) => (
                    <li className="sm-type-caption sm-footnote" key={text}>
                      <Icon className="sm-footnote-icon" size={12} aria-hidden="true" />
                      {text}
                    </li>
                  ))}
                </ul>
                <p className="sm-type-caption sm-muted sm-cta-note">
                  Your information is only used to find and purchase coverage options available for
                  your vehicle.
                </p>
              </div>
            )}
          </div>
        </form>
      </Card>

      <WhyBuyHere />
    </AppShell>
  );
}

const PROMISES: { icon: LucideIcon; text: string }[] = [
  { icon: CheckIcon, text: 'No obligation.' },
  { icon: CheckIcon, text: 'No account required.' },
  { icon: LockIcon, text: 'Secure checkout.' },
];

const REASONS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: DollarSignIcon,
    title: 'Up to 50% less than others',
    text: 'Because you deserve fair, upfront pricing with zero pressure.',
  },
  {
    icon: BanknoteIcon,
    title: 'Easy financing',
    text: '0% financing. No credit check. No worries.',
  },
  {
    icon: GemIcon,
    title: 'Quality coverage',
    text: 'Genuine coverage that delivers when you need it most.',
  },
  {
    icon: ThumbsUpIcon,
    title: 'Your choice',
    text: 'Pick the protection that fits your needs without the extra fluff.',
  },
  {
    icon: ShoppingBagIcon,
    title: 'Shop your way',
    text: "We're open 24/7 — buy coverage how and when it works best for you.",
  },
  {
    icon: UsersIcon,
    title: 'No call centers, no bots',
    text: 'Just real people ready to help when you need it.',
  },
];

function WhyBuyHere() {
  return (
    <section className="sm-stack sm-why" aria-labelledby="sm-why-heading">
      <h2 className="sm-type-title sm-why-heading" id="sm-why-heading">
        Why buying here just makes sense
      </h2>
      <ul className="sm-why-grid">
        {REASONS.map(({ icon: Icon, title, text }) => (
          <li className="sm-panel sm-stack sm-why-card" key={title}>
            <span className="sm-center sm-why-icon">
              <Icon size={22} aria-hidden="true" />
            </span>
            <div className="sm-stack sm-why-text">
              <h3 className="sm-type-heading">{title}</h3>
              <p className="sm-type-body sm-muted">{text}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RateField({
  spec,
  value,
  error,
  onChange,
}: {
  spec: TypedFieldSpec;
  value: string | number | boolean | undefined;
  error?: string;
  onChange: (value: string | number | boolean) => void;
}) {
  const labelAux = spec.required ? 'Required' : undefined;

  if (spec.type === 'boolean') {
    return (
      <FieldFrame label={spec.label} labelAux={labelAux} error={error} hint={spec.helpText}>
        <SegmentedControl<'yes' | 'no'>
          ariaLabel={spec.label}
          options={YES_NO}
          value={value === undefined ? null : value === true ? 'yes' : 'no'}
          onChange={(next) => onChange(next === 'yes')}
        />
      </FieldFrame>
    );
  }

  if (spec.type === 'select') {
    return (
      <Select
        id={`sm-rate-${spec.key}`}
        label={spec.label}
        labelAux={labelAux}
        hint={spec.helpText}
        placeholder="Select"
        options={spec.options ?? []}
        value={value === undefined ? '' : String(value)}
        error={error}
        onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value)}
      />
    );
  }

  return (
    <Field
      id={`sm-rate-${spec.key}`}
      label={spec.label}
      labelAux={labelAux}
      inputMode={spec.type === 'integer' ? 'numeric' : undefined}
      value={value === undefined ? '' : String(value)}
      error={error}
      hint={spec.helpText}
      onInput={(e) => {
        const raw = (e.currentTarget as HTMLInputElement).value;
        onChange(spec.type === 'integer' ? Number(raw.replace(/[^0-9]/g, '')) : raw);
      }}
    />
  );
}
